import { Input, Modal } from 'antd';
import { messages } from '../../../../../messages';
import { ApiErrorDetails } from '../../../../apiErrorDetails/apiErrorDetails';
import { LoadingIndicator } from '../../../../loadingIndicator/loadingIndicator';
import { Form } from '../../../../form/form';
import style from '../styling/widgetRowStyling.module.scss';
import { CustomerWidgetCategorySelect } from '../categories/customerWidgetCategorySelect';
import React, { useEffect, useState } from 'react';
import { useI18n } from '../../../../../hooks/useI18n';
import { useNotificationStore } from '../../../../../hooks/useNotificationStore/notificationStore';
import { useFormik } from 'formik';
import { OrderType, TimePeriodType } from '../../../../../types';
import { ApiError, useApiRequests } from '../../../../../hooks/useApi';
import { ArticleCategoryPicker } from '../../../../pickers/articleCategoryPicker';
import { categoryCodesApi } from '../../../../../api/categoryCodesApi';
import { OrderTypePicker } from '../../../../pickers/orderTypePicker';
import { DeliveryStatusPicker } from '../../../../pickers/deliveryStatusPicker';
import { TimePeriodPicker } from '../../../../pickers/timePeriodPicker';
import { TwitterPicker } from 'react-color';
import { defaultWidgetColors } from '../../../../internal/widgets/widgetColors';
import {
  customerCustomerOrdersReportWidgetApi,
  CreateUpdateCustomerOrdersReportWidgetRequest,
} from '../../../../../api/widgets/customers/customerCustomerOrdersReportWidgetApi';

export const UpdateCustomerOrdersReportWidgetModal = ({
  userWidgetId,
  onCancel,
  onSubmit,
}: {
  userWidgetId: number;
  onCancel: () => void;
  onSubmit: () => void;
}) => {
  const { t } = useI18n();
  const { notify } = useNotificationStore();

  const [name, setName] = useState<string>('');
  const [widgetCategoryId, setWidgetCategoryId] = useState<number | undefined>(
    undefined,
  );
  const [color, setColor] = useState<string | undefined>(undefined);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [orderType, setOrderType] = useState<OrderType>(OrderType.Standard);
  const [deliveryStatus, setDeliveryStatus] = useState<number>(0);
  const [timePeriods, setTimePeriods] = useState<{
    type: TimePeriodType;
    periods: string[];
  }>({
    type: TimePeriodType.Yearly,
    periods: [],
  });

  const [error, setError] = useState<ApiError>();

  const updateWidget = useApiRequests(
    customerCustomerOrdersReportWidgetApi.updateWidget,
  );

  const widget = useApiRequests(
    customerCustomerOrdersReportWidgetApi.getWidget,
  );

  useEffect(() => {
    widget
      .create({
        onSuccess: (response) => {
          setName(response.name);
          setWidgetCategoryId(response.widgetCategoryId);
          setColor(response.color);
          setCategoryIds(response.categoryIds);
          setDeliveryStatus(response.deliveryStatus);
          setOrderType(response.orderType);
          setTimePeriods({
            type: response.timePeriodType,
            periods: response.timePeriods,
          });
        },
        onError: setError,
      })
      .send(userWidgetId);
  }, []);

  const handleFormSubmit = () => {
    const updateWidgetData: CreateUpdateCustomerOrdersReportWidgetRequest = {
      name: name,
      widgetCategoryId: widgetCategoryId,
      color: color,
      categoryIds: categoryIds,
      orderType: orderType,
      deliveryStatus: deliveryStatus,
      timePeriods: timePeriods.periods,
      timePeriodType: timePeriods.type,
    };

    updateWidget
      .create({
        onSuccess: () => {
          notify({
            title: t(messages.widgets.widgetUpdated),
            type: 'success',
          });
          onSubmit();
        },
        onError: setError,
      })
      .send(userWidgetId, updateWidgetData);
  };

  const formik = useFormik<FormData>({
    initialValues: {
      name: '',
      categoryIds: [],
    },
    onSubmit: handleFormSubmit,
  });

  function handleWidgetCategoryOnChange(widgetCategoryId: string | undefined) {
    const id =
      widgetCategoryId === undefined
        ? widgetCategoryId
        : parseInt(widgetCategoryId);
    setWidgetCategoryId(id);
  }

  function handleSelectedCategoryIds(selectedCategoryIds: number[]) {
    setCategoryIds(selectedCategoryIds.slice(0, 300));
  }

  const selectableOrderTypes: OrderType[] = [
    OrderType.Standard,
    OrderType.Sample,
    OrderType.Forecast,
  ];

  return (
    <Modal
      title={t(messages.widgets.customerOrdersReport.update)}
      visible={true}
      closable={false}
      onCancel={onCancel}
      onOk={formik.submitForm}
      okText={t(messages.shared.save)}
      okButtonProps={{ disabled: widget.isLoading || updateWidget.isLoading }}
      width={720}
    >
      {(widget.isLoading || updateWidget.isLoading) && <LoadingIndicator />}
      {error && <ApiErrorDetails error={error} />}
      <Form form={formik}>
        <div className={style.inputRow}>
          <h4>{t(messages.shared.name)}</h4>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t(messages.widgets.widgetNamePlaceholder)}
          />
          <CustomerWidgetCategorySelect
            value={widgetCategoryId?.toString()}
            onChange={handleWidgetCategoryOnChange}
          />
        </div>
        <div className={style.inputRow}>
          <h4>{t(messages.shared.color)}</h4>
          <TwitterPicker
            width="100%"
            triangle="hide"
            colors={defaultWidgetColors}
            color={color}
            onChange={(color) => setColor(color.hex)}
          />
        </div>
        <div className={style.inputRow}>
          <h4>{t(messages.reports.articleCategory)}</h4>
          <ArticleCategoryPicker
            categoryIds={categoryIds}
            onChange={handleSelectedCategoryIds}
            categoryRequestFactory={(_) => categoryCodesApi.getCategories}
          />
        </div>
        <div className={style.inputRow}>
          <h4>{t(messages.order.orderType)}</h4>
          <OrderTypePicker
            orderType={orderType}
            selectableOrderTypes={selectableOrderTypes}
            onChange={setOrderType}
            allowClear={false}
          />
        </div>
        {orderType !== OrderType.Forecast && (
          <div className={style.inputRow}>
            <h4>{t(messages.order.progress)}</h4>
            <DeliveryStatusPicker
              deliveryStatus={deliveryStatus}
              onChange={setDeliveryStatus}
            />
          </div>
        )}
        <div className={style.inputRow}>
          <h4>{t(messages.reports.timePeriod)}</h4>
          <TimePeriodPicker
            selectedTimePeriods={timePeriods.periods}
            selectedTimePeriodsType={timePeriods.type}
            onChange={setTimePeriods}
          />
        </div>
      </Form>
    </Modal>
  );
};

interface FormData {
  name: string;
  categoryIds: number[];
}
